/**
 * SIM mode flag — gates the fetch-boundary mock layer (`mockApi.ts`).
 *
 * When sim mode is on, the two client API wrappers (`orgApiFetch` /
 * `orgApiFetchVoid` in `orgApiFetch.ts` and the private `walletApiFetch`
 * in `clientWallets.ts`) short-circuit to `mockApiFetch` instead of hitting
 * the real org API. The FE — components, hooks, props — stays byte-identical;
 * only the data source swaps.
 *
 * The flag lives in `sessionStorage` so it survives client-side navigation
 * (where the `?sim=` query param drops). `isSimMode()` also re-reads the live
 * URL on every call so the very first query — which can fire before any React
 * effect runs — still sees the flag.
 */

import { IS_PRODUCTION_BUILD } from "@solomon-apps/shared/src/utils/environment";

const SIM_STORAGE_KEY = "yaas:sim";

/** True query-param values that enable sim mode. */
const TRUTHY = new Set(["1", "true"]);
/** Query-param values that explicitly disable sim mode. */
const FALSY = new Set(["0", "false"]);

function hasWindow(): boolean {
    return typeof window !== "undefined";
}

/** Read + persist (or clear) the flag from a `?sim=` param value. */
function applySimParam(value: string | null): boolean | null {
    if (value === null) return null;
    if (TRUTHY.has(value)) {
        setSimMode(true);
        return true;
    }
    if (FALSY.has(value)) {
        setSimMode(false);
        return false;
    }
    return null;
}

/**
 * Returns true when sim mode is active. Checks the persisted
 * `sessionStorage` flag first, then falls back to the live URL — a `?sim=1`
 * / `?sim=true` match persists the flag (so it survives navigation), and a
 * `?sim=0` / `?sim=false` match clears it. SSR-safe.
 */
export function isSimMode(): boolean {
    // Sim mode is a dev/QA affordance and must never activate in production
    // bundles — `?sim=1` would otherwise let any user swap the console onto
    // mock data with mutations that appear to succeed.
    if (IS_PRODUCTION_BUILD) return false;
    if (!hasWindow()) return false;

    // The URL is the most up-to-date source: an explicit `?sim=0` must be
    // able to turn the flag back off even when storage still has it set.
    const fromUrl = applySimParam(new URLSearchParams(window.location.search).get("sim"));
    if (fromUrl !== null) return fromUrl;

    try {
        return window.sessionStorage.getItem(SIM_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

/** Explicitly enable/disable sim mode (persists to `sessionStorage`). */
export function setSimMode(on: boolean): void {
    if (IS_PRODUCTION_BUILD) return;
    if (!hasWindow()) return;
    try {
        if (on) {
            window.sessionStorage.setItem(SIM_STORAGE_KEY, "1");
        } else {
            window.sessionStorage.removeItem(SIM_STORAGE_KEY);
        }
    } catch {
        // sessionStorage can throw in private-mode / sandboxed contexts;
        // sim mode is a dev affordance, so swallow rather than crash.
    }
}

/**
 * Set or clear sim mode from a URL search string (e.g. `location.search`).
 * No-op when the string carries no `?sim=` param.
 */
export function syncSimModeFromUrl(search: string): void {
    if (!hasWindow()) return;
    applySimParam(new URLSearchParams(search).get("sim"));
}
